import { OrganizationUserType } from '@mfw/common';
import { useIntl } from '@mfw/components';

import {
  useOrganizationContext,
  usePermissionsContext,
  useActingUserType,
  useAuthContext,
} from '../../../context';
// By module path, not the `hooks/organizations` barrel: the barrel re-exports
// 16 hooks and their API/react-query layers, and this hook is 6 lines.
import useOrgDisplayName from '../../../hooks/organizations/useOrgDisplayName';
// Same reason: the `hooks/lockerKeys` barrel also carries the board's queries.
import useCanManageLockerKeys from '../../../hooks/lockerKeys/useCanManageLockerKeys';
import { usePremiumReportSubscription } from '../../../hooks/premium';
import { useBuildRoute } from '../../../router/hooks';
import type { SidebarSectionItem } from './SidebarSection/SidebarSection';
import {
  performanceTabAccess,
  PERFORMANCE_TAB_LABELS,
  PERFORMANCE_TABS,
} from '../../../pages/BusinessFinancePages/performanceTabs';
import {
  useCanReadOrgMoney,
  useCanReadTrainerEarnings,
  useOrgMoneyAllowed,
} from '../../../pages/BusinessFinancePages/useCanReadOrgMoney';
import trainerEarningsMessages from '../../../pages/BusinessFinancePages/TrainerEarnings/messages';
import bfMessages from '../../../pages/BusinessFinancePages/messages';
import messages from './messages';
import { useCanReadClients } from '../../../hooks/users/useCanReadClients';
// By module path: the `hooks/tenants` barrel carries every tenant API hook.
import useTenantFeatures from '../../../hooks/tenants/useTenantFeatures';
import { useBrandName } from '../../../hooks/useBrandName';

export interface SidebarSectionData {
  id: string;
  label?: string;
  items: SidebarSectionItem[];
}

/**
 * Builds the org/clients, library, CRM, business-finance and profile
 * sections for the current org context. Which variant (Org Admin vs Trainer
 * profile) a section renders as is derived from `organizations_update` — the
 * same permission the app already uses to gate the Org/Trainer Profile item
 * and pick its label, so this introduces no new role signal.
 */
export function useOrgSidebarSections(): {
  isOrgAdminView: boolean;
  sections: SidebarSectionData[];
} {
  const { formatMessage } = useIntl();
  const brand = useBrandName();
  const { hasPermission } = usePermissionsContext();
  const { currentOrganization, isSingleTrainerOrg } = useOrganizationContext();
  const { currentUser } = useAuthContext();
  const { of } = useBuildRoute();
  // "<Org> Home" / "<Org> Schedule" — one hook decides what stands in for a
  // missing name, same as the two pages' titles.
  const orgName = useOrgDisplayName();

  const organizationId = currentOrganization?.id ?? '';
  const isOrgAdminView = hasPermission('organizations_update');
  // Bundles/Desk/Work Performance/Finances are placeholders for pages that
  // don't exist yet — a client has no staff-facing destination behind any of
  // them, so they stay hidden rather than teasing dead ends. Derived from the
  // account's actual role in this org (not a permission bit), same signal
  // `FeaturedWorkouts`/`ProgramsPage`/etc. already use for this distinction.
  const isClient = useActingUserType() === OrganizationUserType.CLIENT;
  // STAFF has no logical-profile hero (F1.1) — its Home destination is a
  // dedicated page (`staff-home`), not the shared OWNER/TRAINER/CLIENT one.
  const isStaff = useActingUserType() === OrganizationUserType.STAFF;
  const usesOperationalPages = isStaff || currentUser?.isPlatformUser;
  // `!isClient` is what actually decides this, not the permission pair: the
  // seeded `client` role holds `trainers_read` AND `users_read` just like every
  // other org role, so the pair alone let clients into "<Org> Schedule" — a
  // staff view — and the page then 403'd. Their calendar is `/schedule`
  // ("My calendar"), which stays. The permissions are kept so a custom role
  // stripped of either still loses the row.
  const canManageSchedule =
    !isClient && hasPermission('trainers_read') && hasPermission('users_read');
  const canReadClients = useCanReadClients();
  // `client_memberships_manage` alongside the catalog-mutation keys, same
  // reasoning as `useCanReadClients`: front_desk assigns existing memberships
  // to clients from this page without ever creating/editing/deleting a
  // membership type, which the other three keys alone don't cover.
  const canManageMemberships =
    hasPermission('memberships_create') ||
    hasPermission('memberships_update') ||
    hasPermission('memberships_delete') ||
    hasPermission('client_memberships_manage');
  const canReadBusinessStats = hasPermission('stats_org_read');
  // Performance plan 1 (review finding 3) — the actor-class money gate the
  // page uses: `finance_stats_read` AND not an employed trainer
  const canReadOrgMoney = useCanReadOrgMoney(organizationId);
  // T17 (Nivo 4, spec §4.7) — the page behind this row opens on EITHER read
  // key: the five money tabs need `finance_stats_read`, the "Net result" tab
  // needs `finance_entries_read`. A row gated on the first alone would hide the
  // destination from precisely the role the second exists for. The SECTION gate
  // (`isOrgAdminView`) is unchanged: a role without `organizations_update` has
  // no Business & Finance section, and reaches the page by direct link.
  // Performance plan 1, final review (Important 1) — and the actor class:
  // every request behind that page is refused to an employed trainer (B7/B9)
  const orgMoneyAllowed = useOrgMoneyAllowed(organizationId);
  const canReadFinanceStats =
    orgMoneyAllowed &&
    (hasPermission('finance_stats_read') ||
      hasPermission('finance_entries_read'));
  // T15 — same gate `FinancePage` used to admit the Payouts tab with, kept
  // identical so no role that could see its sales as a tab loses that view
  // now that Payouts is a page of its own.
  const canReadPayouts =
    hasPermission('billing_read') || hasPermission('finance_stats_read');
  // Task 4.5 — Platform Analytics disabled at launch, opened by config
  // (PLATFORM_ANALYTICS_ENABLED): a platform user always has it (P3), a
  // regular user only once the BE's `available` flag turns on. The same
  // status call the paywall/card already make, not a second endpoint.
  //
  // Fix round 1 (finding 2) — `enabled` matches the row's OWN gate
  // (`!isClient && hasPermission('stats_premium_read')`, checked again
  // below) so the query never fires for a viewer who could never see the
  // row anyway (a client, or a role without the permission).
  const canShowPlatformAnalyticsRow =
    !isClient && hasPermission('stats_premium_read');
  // Task 7.1 — the status is the SELECTED ORGANIZATION's (it pays); an
  // organization that already HAS it (`hasAccess`, the BE's entitlement —
  // V2-28/6) keeps the row live while the flag is off (ruling V2-9, same rule
  // as the page)
  const premiumReportStatus = usePremiumReportSubscription({
    enabled: canShowPlatformAnalyticsRow,
    organizationId,
  });
  const platformAnalyticsAvailable =
    Boolean(currentUser?.isPlatformUser) ||
    premiumReportStatus.data?.available === true ||
    premiumReportStatus.data?.hasAccess === true;

  const scheduleTo = of(
    usesOperationalPages
      ? '/organizations/:organizationId/staff-schedule'
      : '/organizations/:organizationId/schedule',
    { params: { organizationId } },
  );
  const orgHomeAndScheduleItems: SidebarSectionItem[] = [
    // A client has no org Home in the sidebar — the top-level Home covers it.
    ...(isClient
      ? []
      : [
          {
            label: formatMessage(messages.orgHome),
            icon: 'mdi:home-outline',
            to: of(
              usesOperationalPages
                ? '/organizations/:organizationId/staff-home'
                : '/organizations/:organizationId/home',
              { params: { organizationId } },
            ),
            testId: 'sidebar-org-home',
          },
        ]),
    ...(canManageSchedule
      ? [
          {
            label: formatMessage(messages.orgSchedule),
            icon: 'mdi:calendar-blank',
            to: scheduleTo,
            testId: 'sidebar-org-schedule',
          },
        ]
      : []),
  ];
  const clientsItem: SidebarSectionItem = {
    label: formatMessage(messages.clients),
    icon: 'mdi:user-supervisor',
    to: of('/organizations/:organizationId/clients', {
      params: { organizationId },
    }),
    testId: 'sidebar-clients',
  };
  const membershipsItem: SidebarSectionItem = {
    label: formatMessage(messages.memberships),
    icon: 'mdi:card-account-details-outline',
    to: of('/organizations/:organizationId/memberships', {
      params: { organizationId },
    }),
    testId: 'sidebar-memberships',
  };
  const rolesItem: SidebarSectionItem = {
    label: formatMessage(messages.rolesAndPermissions),
    icon: 'fluent-mdl2:permissions',
    to: of('/organizations/:organizationId/roles', {
      params: { organizationId },
    }),
    testId: 'sidebar-roles-and-permissions',
  };
  // A platform user manages roles from "Tenant admin" (`/admin/roles`) —
  // repeating the row in the org drill-down would be a second door to the
  // same concept.
  const canReadRoles =
    !currentUser?.isPlatformUser &&
    hasPermission('roles_read') &&
    hasPermission('roles_update');
  // "Organization Members" and "Categories" are no longer sidebar rows —
  // they are cards on the Organization Profile page, which links to the same
  // `/organizations/:organizationId/members|categories` pages.
  const bookingEventTemplatesItem: SidebarSectionItem = {
    label: formatMessage(messages.bookingEventTemplates),
    icon: 'mdi:calendar-multiple-check',
    to: of('/organizations/:organizationId/booking-event-templates', {
      params: { organizationId },
    }),
    testId: 'sidebar-booking-event-templates',
  };
  const canReadBookingEventTemplates = hasPermission(
    'booking_event_templates_read',
  );
  /**
   * Group-session statistics. In the business/finance section rather than the
   * Library, where the only other booking row lives: this is a report about the
   * organization, not a content catalogue. Same permission the route guard and
   * the endpoint use, so the row and the page cannot disagree — and the row is
   * not the guard: `BusinessFinancePages/PerformancePage` checks it again
   * before mounting anything.
   */
  const bookingStatsItem: SidebarSectionItem = {
    label: formatMessage(messages.bookingStats),
    icon: 'mdi:calendar-check',
    to: `${of('/organizations/:organizationId/performance', {
      params: { organizationId },
    })}?tab=sessions`,
    testId: 'sidebar-booking-stats',
  };
  const canReadBookingStats = hasPermission('booking_events_read');
  // The tenant's `sessions` switch: session statistics are the feature's own
  // report and go with it. `booking_events_read` is KEPT while it is off
  // (existing sessions stay manageable), so the chokepoint alone would leave
  // this row; the template row needs no such check (its key is mapped).
  const sessionsOn = useTenantFeatures().isOn('sessions');
  // `financeItem` ("Billing & Subscriptions") used to live here — moved into
  // the account menu (`SettingsDropdown`, T11): it names the relationship
  // with MyFitWorld, not the gym's own business, which is what this section
  // is about.
  // Q10 (Task 12) — the Wallet row itself is REMOVED from here: it now lives
  // in the account area (Account settings → Subscriptions, personal cards
  // tab), reachable from the account menu the same way "Billing &
  // Subscriptions" already is. The standalone `/wallet` route still
  // redirects there, so no old link breaks — only the sidebar's own entry
  // point is gone. The Payouts row below is untouched by this change.
  // T15 — "Payouts": the organization's marketplace sales and payout
  // onboarding, moved off the billing page's tab strip.
  const payoutsItem: SidebarSectionItem = {
    label: formatMessage(bfMessages.marketplacePayouts),
    icon: 'mdi:bank-transfer-out',
    to: of('/organizations/:organizationId/marketplace-payouts', {
      params: { organizationId },
    }),
    testId: 'sidebar-payouts',
  };

  // STAFF role plan Phase 5 — "Pult/Desk". Permission-gated like
  // `payoutsItem`/`bookingStatsItem` above (shared across both
  // `businessFinanceSection` variants below), not tied to `isOrgAdminView`:
  // front_desk holds `desk_products_manage`/`desk_sales_manage` but not
  // `organizations_update`, so it needs this item in the NON-admin variant
  // too, unlike the section's other rows.
  // A single trainer org has no front desk — it never sees Desk, whatever
  // permissions it holds.
  // A super admin can also switch Desk off per organization (`deskEnabled`,
  // on unless explicitly false).
  const canUseDesk =
    !isSingleTrainerOrg &&
    currentOrganization?.deskEnabled !== false &&
    (hasPermission('desk_products_manage') ||
      hasPermission('desk_sales_manage'));
  const deskItem: SidebarSectionItem = {
    label: formatMessage(messages.desk),
    icon: 'mdi:point-of-sale',
    to: of('/organizations/:organizationId/desk', {
      params: { organizationId },
    }),
    testId: 'sidebar-desk',
  };
  // Locker keys ("Keys"): the page's own gate (`locker_keys_manage`, unless a
  // super admin switched Keys off for the organization), so the row and the
  // page cannot disagree. Never the desk pair, so a role may hold either
  // without the other, and never `isSingleTrainerOrg` — the solo-PT owner assigns
  // keys too (locker-keys spec D1). Listed right after Desk in every branch
  // below.
  const canUseLockerKeys = useCanManageLockerKeys();
  // MV2-577 Part 2 (3.4) — "Trainer earnings": the page's own gate (its key
  // or a platform user, AND not an employed trainer), never
  // `finance_stats_read` — a front desk reads fees without revenue — and never
  // the fee-input roster, so a now-solo organization keeps its history.
  // Offered in every layout a holder of the key lands in: both Business &
  // Finance variants below and the STAFF layout (front desk).
  const canReadTrainerEarnings = useCanReadTrainerEarnings(organizationId);
  const trainerEarningsItem: SidebarSectionItem = {
    label: formatMessage(trainerEarningsMessages.title),
    icon: 'mdi:account-cash-outline',
    to: of('/organizations/:organizationId/trainer-earnings', {
      params: { organizationId },
    }),
    testId: 'sidebar-trainer-earnings',
  };
  const lockerKeysItem: SidebarSectionItem = {
    label: formatMessage(messages.lockerKeys),
    icon: 'mdi:key-variant',
    to: of('/organizations/:organizationId/desk/keys', {
      params: { organizationId },
    }),
    testId: 'sidebar-locker-keys',
  };

  const orgClientsSection: SidebarSectionData = isOrgAdminView
    ? {
        id: 'org',
        label: orgName,
        items: [
          ...orgHomeAndScheduleItems,
          ...(canReadClients ? [clientsItem] : []),
          ...(canReadRoles ? [rolesItem] : []),
          ...(canManageMemberships ? [membershipsItem] : []),
          // Moved out of Business & Finance (PO, 25.09.2026) — see there.
          ...(canUseDesk ? [deskItem] : []),
          ...(canUseLockerKeys ? [lockerKeysItem] : []),
        ],
      }
    : {
        id: 'clients',
        // A client manages no clients — the caption would sit over a lone
        // "Org Home" row, so they get the rows without a header.
        label: isClient ? undefined : formatMessage(messages.clients),
        items: [
          ...orgHomeAndScheduleItems,
          ...(canReadClients ? [clientsItem] : []),
          ...(canReadRoles ? [rolesItem] : []),
          ...(canManageMemberships ? [membershipsItem] : []),
        ],
      };

  const librarySection: SidebarSectionData = {
    id: 'library',
    label: formatMessage(messages.library),
    items: [
      ...(hasPermission('workouts_create')
        ? [
            {
              label: formatMessage(messages.workoutContent),
              icon: 'mdi:dumbbell',
              to: of('/organizations/:organizationId/library', {
                params: { organizationId },
                search: 'tab=workouts',
              }),
              testId: 'sidebar-library',
            },
            {
              label: formatMessage(messages.nutrition),
              icon: 'mdi:food-apple-outline',
              to: of('/organizations/:organizationId/nutrition', {
                params: { organizationId },
                search: 'tab=meals',
              }),
              testId: 'sidebar-nutrition',
            },
          ]
        : []),
      ...(hasPermission('education_create')
        ? [
            {
              label: formatMessage(messages.education),
              icon: 'mdi:school-outline',
              to: of('/organizations/:organizationId/education', {
                params: { organizationId },
                search: 'tab=episodes',
              }),
              testId: 'sidebar-education',
            },
          ]
        : []),
      ...(!isClient
        ? [
            {
              label: formatMessage(messages.bundles),
              icon: 'mdi:layers-outline',
              testId: 'sidebar-bundles',
              soon: true,
            },
          ]
        : []),
      ...(canReadBookingEventTemplates ? [bookingEventTemplatesItem] : []),
      // Moved out of the account-level top items (SidebarTopItems) for
      // OWNER/TRAINER only — a CLIENT membership and the personal profile
      // still get it up there. Platform users never get a personal Saved
      // list (P6: their org-less context is authoring/curation, not a
      // consumer profile), so they're excluded here too.
      ...(!isClient && !currentUser?.isPlatformUser
        ? [
            {
              label: formatMessage(messages.saved),
              icon: 'mdi:bookmark-outline',
              to: of('/saved'),
              testId: 'sidebar-saved',
            },
          ]
        : []),
    ],
  };

  const canReadNotificationCenter = hasPermission('notification_center_read');
  const notificationCenterItem: SidebarSectionItem = {
    label: formatMessage(messages.notificationCenter),
    icon: 'mdi:bell-outline',
    to: of('/organizations/:organizationId/notification-center', {
      params: { organizationId },
    }),
    testId: 'sidebar-notification-center',
  };
  const crmSection: SidebarSectionData = {
    id: 'crm',
    label: formatMessage(messages.crm),
    items: [
      ...(canReadNotificationCenter ? [notificationCenterItem] : []),
      ...(hasPermission('questionnaires_read')
        ? [
            {
              label: formatMessage(messages.questionnaires),
              icon: 'mdi:clipboard-text-outline',
              to: of('/organizations/:organizationId/questionnaires', {
                params: { organizationId },
              }),
              testId: 'sidebar-questionnaires',
            },
          ]
        : []),
    ],
  };

  // Business & Finance (PO, 25.09.2026): Performance (with its sub-pages) ·
  // Profits & Expenses · Marketplace Payouts · MyFitWorld Platform Analytics.
  // Each sub-page is a `?tab=` of Performance, offered on the same keys the
  // page itself uses to show that tab.
  const performanceTo = of('/organizations/:organizationId/performance', {
    params: { organizationId },
  });
  // Performance plan 1 (review findings 1 and 3) — the page's own tab list,
  // labels and gate (`performanceTabs.ts`), so the two can never drift
  // again: same order (Booking Sessions right after Trainings), same
  // actor-class money gate (an employed trainer gets no money sub-page)
  const performanceAccess = performanceTabAccess({
    canReadOrgMoney,
    canReadSessions: canReadBookingStats,
    canReadWork: canReadBusinessStats,
    sessionsOn,
  });
  const performanceChildren: SidebarSectionItem[] = PERFORMANCE_TABS.filter(
    (tab) => performanceAccess[tab],
  ).map((tab) => ({
    label: formatMessage(PERFORMANCE_TAB_LABELS[tab]),
    icon: 'mdi:circle-small',
    to: `${performanceTo}?tab=${tab}`,
    testId: `sidebar-performance-${tab}`,
  }));
  const businessFinanceSection: SidebarSectionData = isOrgAdminView
    ? {
        id: 'bf',
        label: formatMessage(messages.businessAndFinance),
        items: [
          ...(performanceChildren.length > 0
            ? [
                {
                  label: formatMessage(bfMessages.performance),
                  icon: 'mdi:chart-box-outline',
                  to: performanceTo,
                  testId: 'sidebar-performance',
                  children: performanceChildren,
                },
              ]
            : []),
          ...(canReadTrainerEarnings ? [trainerEarningsItem] : []),
          ...(canReadFinanceStats
            ? [
                {
                  label: formatMessage(bfMessages.profitsAndExpenses),
                  icon: 'mdi:finance',
                  to: of('/organizations/:organizationId/profits-expenses', {
                    params: { organizationId },
                  }),
                  testId: 'sidebar-profits-expenses',
                },
              ]
            : []),
          ...(canReadPayouts ? [payoutsItem] : []),
          ...(canShowPlatformAnalyticsRow
            ? [
                {
                  label: formatMessage(bfMessages.platformAnalytics, { brand }),
                  icon: 'mdi:chart-timeline-variant-shimmer',
                  testId: 'sidebar-platform-analytics',
                  badge: formatMessage(bfMessages.platformAnalyticsPrice),
                  ...(platformAnalyticsAvailable
                    ? {
                        to: of(
                          '/organizations/:organizationId/platform-analytics',
                          { params: { organizationId } },
                        ),
                      }
                    : { soon: true }),
                },
              ]
            : []),
          // Desk is NOT here any more (PO, 25.09.2026): the owner's desk
          // settings belong to the organization, the live desk to Staff.
          // Until those screens exist it sits in the organization section.
        ],
      }
    : {
        id: 'bf',
        // 4.1 (PO 25.09): one name for every role — the owner's branch above
        // already reads "Business & Finance"
        label: formatMessage(messages.businessAndFinance),
        items: [
          ...(canReadPayouts ? [payoutsItem] : []),
          // BEFORE the two placeholders below: they announce pages that do not
          // exist yet, and a real destination listed under two dead ones is
          // where a reader stops scanning. `!isClient`, same as those
          // placeholders: a report about the organization's staff, not a
          // consumer-facing page.
          ...(!isClient && canReadBookingStats && sessionsOn
            ? [bookingStatsItem]
            : []),
          ...(canReadTrainerEarnings ? [trainerEarningsItem] : []),
          // front_desk lands in THIS variant (no `organizations_update`), so
          // Desk has to be repeated here rather than living only in the admin
          // variant above.
          ...(canUseDesk ? [deskItem] : []),
          ...(canUseLockerKeys ? [lockerKeysItem] : []),
          // The two announcements preview the trainer's own work and earnings
          // pages (3.5). An assistant trainer only assigns workouts and
          // coaches clients (PO 01.10) — no stats of their own, so no
          // announcements: `stats_clients_read` is what a trainer holds and
          // an assistant does not.
          ...(!isClient && hasPermission('stats_clients_read')
            ? [
                {
                  label: formatMessage(messages.workPerformance),
                  icon: 'mdi:trending-up',
                  testId: 'sidebar-work-performance',
                  soon: true,
                },
                {
                  label: formatMessage(messages.finances),
                  icon: 'mdi:cash-multiple',
                  testId: 'sidebar-finances',
                  soon: true,
                },
              ]
            : []),
        ],
      };

  const profileSection: SidebarSectionData = {
    id: 'profile',
    label: formatMessage(messages.profileSection),
    items: [
      {
        label: formatMessage(messages.myProfile),
        icon: 'mdi:account-circle-outline',
        // `MyOrgProfile` redirects this to `/members/:id` or `/clients/:id`
        // by org-user type; that destination is also reachable from
        // "Organization Members"/"Clients", so it's the one that highlights
        // (see `isNavAncestor` in NavigationItem) — this item stays as its
        // own conceptual `/me` link rather than duplicating that resolution.
        to: of('/organizations/:organizationId/me/:orgUserId', {
          params: { organizationId, orgUserId: currentUser?.id ?? '' },
        }),
        testId: 'sidebar-my-profile',
      },
      ...(isOrgAdminView
        ? [
            {
              label: isSingleTrainerOrg
                ? formatMessage(messages.trainerProfile)
                : formatMessage(messages.organizationProfile),
              icon: 'mdi:building',
              to: of('/organizations/:organizationId', {
                params: { organizationId },
              }),
              testId: 'sidebar-organization-profile',
            },
          ]
        : []),
      ...(hasPermission('org_rebrand_manage')
        ? [
            {
              label: formatMessage(messages.appBranding),
              icon: 'mdi:palette-outline',
              to: of('/organizations/:organizationId/branding', {
                params: { organizationId },
              }),
              testId: 'sidebar-org-branding',
            },
          ]
        : []),
    ],
  };

  // STAFF's own layout: standalone sections for Booking templates and Desk
  // (pulled out of Library/Business & Finance, which STAFF doesn't otherwise
  // see), and a CRM section trimmed to Notification center only
  // (Questionnaires is an admin/trainer-facing content tool). Poslovni
  // Prikaz (Finance/Trainers) is not a sidebar destination of its own — it's
  // folded into Staff Home as two extra tabs, reachable through the existing
  // Home row. Payouts and Booking stats stay out of the STAFF sidebar even
  // though front_desk may hold their permissions — only Desk is a
  // front_desk-facing destination here.
  const staffBookingSection: SidebarSectionData = {
    id: 'staff-booking',
    label: formatMessage(messages.bookingEventTemplates),
    items: canReadBookingEventTemplates ? [bookingEventTemplatesItem] : [],
  };
  const staffCrmSection: SidebarSectionData = {
    id: 'crm',
    label: formatMessage(messages.crm),
    items: canReadNotificationCenter ? [notificationCenterItem] : [],
  };
  const staffDeskSection: SidebarSectionData = {
    id: 'staff-desk',
    label: formatMessage(messages.desk),
    items: [
      ...(canUseDesk ? [deskItem] : []),
      ...(canUseLockerKeys ? [lockerKeysItem] : []),
    ],
  };

  // MV2-577 Part 2 — a front desk (STAFF) never sees the Business & Finance
  // section above, so the one finance report it reads gets its own.
  const staffBusinessFinanceSection: SidebarSectionData = {
    id: 'staff-bf',
    label: formatMessage(messages.businessAndFinance),
    items: canReadTrainerEarnings ? [trainerEarningsItem] : [],
  };

  return {
    isOrgAdminView,
    sections: isStaff
      ? [
          orgClientsSection,
          staffBookingSection,
          staffCrmSection,
          staffDeskSection,
          staffBusinessFinanceSection,
          profileSection,
        ]
      : [
          orgClientsSection,
          librarySection,
          crmSection,
          businessFinanceSection,
          profileSection,
        ],
  };
}
